import React, { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Flag,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Ban,
  Eye,
  RefreshCw,
  Filter,
  ChevronDown,
  ChevronUp,
  User,
  Calendar,
  MessageSquare,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

interface UserReport {
  id: string;
  reporter_id: string;
  reported_id: string;
  reason: string;
  details: string | null;
  context: string | null;
  context_id: string | null;
  status: string;
  created_at: string;
  reviewed_at: string | null;
  resolved_action: string | null;
  resolution_notes: string | null;
  reporter?: {
    name: string;
    photo_url: string | null;
  };
  reported?: {
    name: string;
    photo_url: string | null;
  };
}

const REASON_LABELS: Record<string, string> = {
  inappropriate_content: "Inappropriate Content",
  harassment: "Harassment",
  spam: "Spam",
  fake_profile: "Fake Profile",
  scam: "Scam or Fraud",
  underage: "Underage User",
  other: "Other",
};

const ACTION_OPTIONS = [
  { value: "dismissed", label: "Dismiss Report", color: "text-gray-600" },
  { value: "warned", label: "Warn User", color: "text-amber-600" },
  { value: "banned_24h", label: "Ban 24 Hours", color: "text-orange-600" },
  { value: "banned_72h", label: "Ban 72 Hours", color: "text-red-500" },
  { value: "banned_7d", label: "Ban 7 Days", color: "text-red-600" },
  { value: "permanent_ban", label: "Permanent Ban", color: "text-red-700" },
];

const AdminReportsPanel: React.FC = () => {
  const { user } = useAuthContext();
  const [reports, setReports] = useState<UserReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<"pending" | "reviewed" | "all">("pending");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [actionModal, setActionModal] = useState<{ reportId: string; reportedId: string; reportedName: string } | null>(null);
  const [selectedAction, setSelectedAction] = useState<string>("");
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [processing, setProcessing] = useState(false);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from("user_reports")
        .select(`
          *,
          reporter:profiles!user_reports_reporter_id_fkey(name, photo_url),
          reported:profiles!user_reports_reported_id_fkey(name, photo_url)
        `)
        .order("created_at", { ascending: false });

      if (filter === "pending") {
        query = query.eq("status", "pending");
      } else if (filter === "reviewed") {
        query = query.neq("status", "pending");
      }

      const { data, error } = await query.limit(100);

      if (error) throw error;
      setReports(data || []);
    } catch (error) {
      console.error("Error fetching reports:", error);
      toast({
        title: "Error",
        description: "Failed to fetch reports",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === reports.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(reports.map((r) => r.id)));
    }
  };

  const handleAction = async (action: string, reportIds: string[], reportedId?: string) => {
    if (!user) return;

    setProcessing(true);
    try {
      // Update report status
      const { error: updateError } = await supabase
        .from("user_reports")
        .update({
          status: "reviewed",
          reviewed_at: new Date().toISOString(),
          reviewed_by: user.id,
          resolved_action: action,
          resolution_notes: resolutionNotes || null,
        })
        .in("id", reportIds);

      if (updateError) throw updateError;

      // If banning, create ban record
      if (action.startsWith("banned_") || action === "permanent_ban") {
        const banDurations: Record<string, string | null> = {
          banned_24h: "24 hours",
          banned_72h: "72 hours",
          banned_7d: "7 days",
          permanent_ban: null,
        };

        // Get unique reported users from selected reports
        const reportedUsers = new Set(
          reports
            .filter((r) => reportIds.includes(r.id))
            .map((r) => r.reported_id)
        );

        for (const userId of reportedUsers) {
          const expiresAt = banDurations[action]
            ? new Date(Date.now() + parseDuration(banDurations[action]!)).toISOString()
            : null;

          const { error: banError } = await supabase.from("user_bans").insert({
            user_id: userId,
            banned_by: user.id,
            ban_type: "booth_access",
            reason: `Banned by admin: ${action}`,
            report_ids: reportIds,
            expires_at: expiresAt,
            is_permanent: action === "permanent_ban",
          });

          if (banError) console.error("Ban error:", banError);

          // Notify the user
          await supabase.from("notifications").insert({
            user_id: userId,
            type: "ban",
            title: action === "permanent_ban" ? "🚫 Account Suspended" : "⚠️ Temporary Restriction",
            body: action === "permanent_ban"
              ? "Your account has been suspended due to policy violations."
              : `Your booth access has been restricted for ${banDurations[action]}.`,
          });
        }
      }

      toast({
        title: "✅ Action Completed",
        description: `${reportIds.length} report(s) processed`,
      });

      setSelectedIds(new Set());
      setActionModal(null);
      setSelectedAction("");
      setResolutionNotes("");
      fetchReports();
    } catch (error) {
      console.error("Error processing action:", error);
      toast({
        title: "Error",
        description: "Failed to process action",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  const parseDuration = (duration: string): number => {
    const match = duration.match(/(\d+)\s*(hours?|days?)/);
    if (!match) return 0;
    const value = parseInt(match[1]);
    const unit = match[2];
    if (unit.startsWith("hour")) return value * 60 * 60 * 1000;
    if (unit.startsWith("day")) return value * 24 * 60 * 60 * 1000;
    return 0;
  };

  const pendingCount = reports.filter((r) => r.status === "pending").length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Flag className="w-6 h-6 text-red-500" />
          <h2 className="text-xl font-bold">User Reports</h2>
          {pendingCount > 0 && (
            <Badge className="bg-red-500 text-white">{pendingCount} pending</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={(v: any) => setFilter(v)}>
            <SelectTrigger className="w-32">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="reviewed">Reviewed</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={fetchReports}>
            <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
          </Button>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 p-3 bg-muted rounded-xl">
          <span className="text-sm font-medium">{selectedIds.size} selected</span>
          <div className="flex-1" />
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleAction("dismissed", Array.from(selectedIds))}
            disabled={processing}
          >
            <XCircle className="w-4 h-4 mr-1" />
            Dismiss All
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => {
              setActionModal({
                reportId: "bulk",
                reportedId: "",
                reportedName: `${selectedIds.size} users`,
              });
            }}
            disabled={processing}
          >
            <Ban className="w-4 h-4 mr-1" />
            Take Action
          </Button>
        </div>
      )}

      {/* Reports List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : reports.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No reports to review</p>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Select All */}
          <div className="flex items-center gap-3 p-2 border-b">
            <Checkbox
              checked={selectedIds.size === reports.length && reports.length > 0}
              onCheckedChange={selectAll}
            />
            <span className="text-sm text-muted-foreground">Select all</span>
          </div>

          {reports.map((report) => (
            <div
              key={report.id}
              className={cn(
                "border rounded-xl transition-all",
                report.status === "pending" ? "bg-card" : "bg-muted/30",
                expandedId === report.id && "ring-2 ring-primary"
              )}
            >
              {/* Report Row */}
              <div className="flex items-center gap-3 p-3">
                <Checkbox
                  checked={selectedIds.has(report.id)}
                  onCheckedChange={() => toggleSelect(report.id)}
                />

                {/* Reported User */}
                <Avatar className="w-10 h-10">
                  <AvatarImage src={report.reported?.photo_url || undefined} />
                  <AvatarFallback>{report.reported?.name?.charAt(0) || "?"}</AvatarFallback>
                </Avatar>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{report.reported?.name || "Unknown"}</span>
                    <Badge
                      variant="outline"
                      className={cn(
                        "text-xs",
                        report.reason === "harassment" && "border-red-300 text-red-600",
                        report.reason === "scam" && "border-orange-300 text-orange-600",
                        report.reason === "underage" && "border-purple-300 text-purple-600"
                      )}
                    >
                      {REASON_LABELS[report.reason] || report.reason}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>by {report.reporter?.name || "Unknown"}</span>
                    <span>•</span>
                    <span>{format(new Date(report.created_at), "MMM d, h:mm a")}</span>
                  </div>
                </div>

                {/* Status */}
                {report.status === "pending" ? (
                  <Badge className="bg-amber-100 text-amber-700">
                    <Clock className="w-3 h-3 mr-1" />
                    Pending
                  </Badge>
                ) : (
                  <Badge
                    className={cn(
                      report.resolved_action === "dismissed" && "bg-gray-100 text-gray-600",
                      report.resolved_action === "warned" && "bg-amber-100 text-amber-600",
                      report.resolved_action?.includes("banned") && "bg-red-100 text-red-600"
                    )}
                  >
                    <CheckCircle className="w-3 h-3 mr-1" />
                    {report.resolved_action || "Reviewed"}
                  </Badge>
                )}

                {/* Actions */}
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setExpandedId(expandedId === report.id ? null : report.id)}
                  >
                    {expandedId === report.id ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </Button>
                  {report.status === "pending" && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleAction("dismissed", [report.id])}
                        disabled={processing}
                      >
                        <XCircle className="w-4 h-4 text-gray-500" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                          setActionModal({
                            reportId: report.id,
                            reportedId: report.reported_id,
                            reportedName: report.reported?.name || "User",
                          })
                        }
                        disabled={processing}
                      >
                        <Ban className="w-4 h-4 text-red-500" />
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Expanded Details */}
              {expandedId === report.id && (
                <div className="px-4 pb-4 pt-2 border-t space-y-3">
                  {report.details && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Details</p>
                      <p className="text-sm bg-muted p-2 rounded-lg">{report.details}</p>
                    </div>
                  )}
                  {report.context && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <MessageSquare className="w-3 h-3" />
                      <span>Context: {report.context}</span>
                    </div>
                  )}
                  {report.resolution_notes && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Resolution Notes</p>
                      <p className="text-sm bg-green-50 p-2 rounded-lg text-green-800">
                        {report.resolution_notes}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Action Modal */}
      <Dialog open={!!actionModal} onOpenChange={(open) => !open && setActionModal(null)}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ban className="w-5 h-5 text-red-500" />
              Take Action
            </DialogTitle>
            <DialogDescription>
              Choose an action for {actionModal?.reportedName}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Action</label>
              <Select value={selectedAction} onValueChange={setSelectedAction}>
                <SelectTrigger>
                  <SelectValue placeholder="Select action..." />
                </SelectTrigger>
                <SelectContent>
                  {ACTION_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <span className={option.color}>{option.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Resolution Notes (Optional)</label>
              <Textarea
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                placeholder="Add notes about this action..."
                className="resize-none h-20"
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setActionModal(null)}
                disabled={processing}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  const ids =
                    actionModal?.reportId === "bulk"
                      ? Array.from(selectedIds)
                      : [actionModal?.reportId!];
                  handleAction(selectedAction, ids, actionModal?.reportedId);
                }}
                disabled={!selectedAction || processing}
                className="flex-1 bg-red-500 hover:bg-red-600"
              >
                {processing ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  "Confirm Action"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminReportsPanel;
